import { Link } from '@tanstack/react-router';
import {
  ArrowRight,
  BarChart3,
  BrainCircuit,
  Check,
  CheckSquare,
  Clock3,
  FileText,
  Hash,
  Languages,
  Mail,
  MessageSquareOff,
  PenLine,
  ShieldCheck,
  Sparkles,
  Star,
  type LucideIcon,
} from 'lucide-react';
import { Badge, Button, Card, CardContent, formatMoney } from '@marketingbee/ui';
import { trpc } from '../lib/trpc';
import { Wordmark } from '../shell/Logo';

// Public landing page at `/` (signed-in visitors go straight to /dashboard, see routes.tsx).
// Copy matches wrkrbz.com/marketingbee so the two never disagree.

type Feature = { icon: LucideIcon; title: string; body: string; points: string[] };

const FEATURES: Feature[] = [
  {
    icon: BrainCircuit,
    title: 'Brand Brain',
    body: 'Tell MarketingBee once who you are, who you talk to, what you post about and what you never say. Every draft starts from it.',
    points: ['Audiences, content pillars and your voice', 'Banned phrases and claims you can back up', 'Pillars you always write yourself'],
  },
  {
    icon: CheckSquare,
    title: 'Nothing posts without you',
    body: 'Each week it plans and drafts your posts and emails, checks them against your guardrails, and waits for a person to approve them.',
    points: ['A review queue with approve, change or reject', 'Editing approved content sends it back for approval', 'Every decision kept in the history'],
  },
  {
    icon: BarChart3,
    title: 'Results you can trust',
    body: 'Every link is tracked, bot clicks are left out, and sign-ups your site reports are credited to the post that brought them.',
    points: ['Clicks and sign-ups by channel and pillar', 'Your top posts for the period', 'Short links for your bio and QR codes'],
  },
];

const STEPS: { title: string; body: string }[] = [
  { title: 'Set up your brand', body: 'Tell it who you sell to, your content pillars and the words you never use.' },
  { title: 'A weekly plan', body: 'Posts for Instagram, Facebook, LinkedIn and Bluesky, a newsletter and a blog post, drafted in your voice.' },
  { title: 'You approve', body: 'Nothing goes out until a person says yes. Edit, ask for changes or reject, all in one weekly digest.' },
  { title: 'Publish and measure', body: 'Posts go out through Konekto Post’s official integrations, every link is tracked, and you see sign-ups by channel and pillar.' },
];

const GUARDRAILS: { icon: LucideIcon; title: string; body: string }[] = [
  { icon: Star, title: 'Never writes reviews or testimonials', body: 'Social proof has to come from real customers, so it won’t draft any.' },
  { icon: Hash, title: 'No invented numbers', body: 'No made-up statistics and no “#1” claims. If a figure isn’t in your Brand Brain, it isn’t in the post.' },
  { icon: MessageSquareOff, title: 'No engagement bait', body: 'No “comment YES below” tricks and no automatic DMs to people who interact with you.' },
  { icon: PenLine, title: 'Your founder voice stays yours', body: 'Mark a pillar human-only and MarketingBee leaves it for you to write.' },
  { icon: ShieldCheck, title: 'CASL-ready email', body: 'Double opt-in, a consent ledger and one-click unsubscribe on every message.' },
  { icon: Languages, title: 'English and Canadian French', body: 'Plans and drafts in either language, or both.' },
];

const CHANNELS: { title: string; body: string }[] = [
  { title: 'Instagram, Facebook and LinkedIn', body: 'Published through Konekto Post’s approved Meta and LinkedIn apps.' },
  { title: 'Bluesky', body: 'Connects directly to your account.' },
  { title: 'Email', body: 'Newsletters to your opted-in contacts, with consent tracked.' },
  { title: 'Blog', body: 'Full blog post drafts, ready for you to review and publish.' },
];

const FAQ: { q: string; a: string }[] = [
  { q: 'Does it post without me?', a: 'No. Every post, email and blog draft needs a person’s approval before it goes out.' },
  {
    q: 'Which accounts can it post to?',
    a: 'Instagram, Facebook and LinkedIn through Konekto Post, Bluesky directly, plus email to your opted-in contacts and drafts for your blog.',
  },
  {
    q: 'Do I need a Konekto account?',
    a: 'Yes, for Instagram, Facebook and LinkedIn. A free Konekto Post account connects them through Konekto’s approved Meta and LinkedIn apps. Bluesky connects directly.',
  },
  {
    q: 'Is it CASL compliant?',
    a: 'It’s built for it: double opt-in, a consent ledger and one-click unsubscribe. You still own your consent records and how you collect them.',
  },
  { q: 'Can I write some posts myself?', a: 'Yes. Write any post yourself, or mark a whole pillar human-only so MarketingBee never drafts it.' },
  { q: 'What happens after the free trial?', a: 'Pick a plan to keep going. Pay monthly, change plans or cancel any time.' },
];

/** A static picture of the review queue: what a week of drafts looks like before approval. */
function ReviewPreview() {
  const items = [
    { channel: 'Instagram', pillar: 'Behind the scenes', when: 'Tue 9:30', text: 'Three things we check before every creator brief goes out, and why the third one saves a week.', state: 'ready' as const },
    { channel: 'LinkedIn', pillar: 'Operator playbooks', when: 'Wed 8:00', text: 'How a 4-person skincare brand runs a monthly creator program without a spreadsheet.', state: 'ready' as const },
    { channel: 'Email', pillar: 'Newsletter', when: 'Thu 7:00', text: 'This week: the brief template, two campaign ideas for spring, and what we learned from 40 pitches.', state: 'flag' as const },
  ];
  return (
    <div aria-hidden className="relative min-w-0">
      <div className="absolute -inset-2 rounded-[28px] bg-primary/10 blur-2xl sm:-inset-6" />
      <div className="relative rounded-2xl border border-border bg-card p-4 text-left shadow-2xl">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-[13px] font-semibold">
            <Sparkles className="size-4 text-primary" /> Waiting for review
          </div>
          <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[12px] font-semibold text-primary">3 this week</span>
        </div>
        <ul className="space-y-2.5">
          {items.map((i) => (
            <li key={i.channel} className="rounded-xl border border-border bg-background/60 p-3">
              <div className="flex flex-wrap items-center justify-between gap-x-3 text-[12px] text-muted-foreground">
                <span>
                  <span className="font-semibold text-foreground">{i.channel}</span> · {i.pillar}
                </span>
                <span className="flex items-center gap-1">
                  <Clock3 className="size-3.5" /> {i.when}
                </span>
              </div>
              <p className="mt-1.5 text-[13.5px] leading-snug">{i.text}</p>
              <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
                {i.state === 'flag' ? (
                  <span className="text-[12px] text-warning">Check: “40 pitches” is not in your approved claims</span>
                ) : (
                  <span className="flex items-center gap-1 text-[12px] text-success">
                    <Check className="size-3.5" /> Passed brand checks
                  </span>
                )}
                <span className="ml-auto flex gap-1.5">
                  <span className="rounded-md border border-border px-2 py-1 text-[12px]">Edit</span>
                  <span className="rounded-md bg-primary px-2 py-1 text-[12px] font-semibold text-primary-foreground">Approve</span>
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function SectionTitle({ id, eyebrow, title, sub }: { id: string; eyebrow: string; title: string; sub?: string }) {
  return (
    <div className="mx-auto max-w-2xl text-center">
      <p className="text-[13px] font-semibold uppercase tracking-[0.12em] text-primary">{eyebrow}</p>
      <h2 id={id} className="mt-2 text-[30px] font-semibold tracking-[-0.02em] sm:text-[34px]">
        {title}
      </h2>
      {sub ? <p className="mt-3 text-[16px] text-muted-foreground">{sub}</p> : null}
    </div>
  );
}

export function LandingPage() {
  const plans = trpc.plans.useQuery();
  const trialDays = plans.data?.trialDays;
  const trialLabel = `Start ${trialDays ? `${trialDays}-day ` : ''}free trial`;
  return (
    <div className="min-h-dvh overflow-x-clip bg-background">
      <header className="sticky top-0 z-10 border-b border-transparent bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
        <div className="mx-auto flex h-16 max-w-[1100px] items-center justify-between px-4 sm:px-8">
          <Link to="/" aria-label="MarketingBee home" className="shrink-0">
            <Wordmark />
          </Link>
          <nav className="flex items-center gap-1 sm:gap-2">
            <Button asChild variant="ghost" className="hidden md:inline-flex">
              <a href="#how">How it works</a>
            </Button>
            <Button asChild variant="ghost" className="hidden md:inline-flex">
              <a href="#guardrails">Guardrails</a>
            </Button>
            <Button asChild variant="ghost" className="hidden sm:inline-flex">
              <Link to="/pricing">Pricing</Link>
            </Button>
            <Button asChild variant="ghost">
              <Link to="/sign-in">Sign in</Link>
            </Button>
            <Button asChild>
              <Link to="/sign-up">Start free trial</Link>
            </Button>
          </nav>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="mx-auto grid max-w-[1100px] items-center gap-12 px-4 pb-20 pt-14 sm:px-8 sm:pt-20 lg:grid-cols-[1.05fr_1fr]">
          <div className="min-w-0 text-center lg:text-left">
            <Badge variant="primary">Marketing AI by WRKRBZ</Badge>
            <h1 className="mt-4 text-[40px] font-semibold leading-[1.08] tracking-[-0.03em] sm:text-[56px]">Your AI marketing team</h1>
            <p className="mt-5 max-w-xl text-[17px] text-muted-foreground sm:text-[19px] lg:max-w-none">
              Plans, drafts and publishes your posts and emails after you approve them, then shows what worked.
            </p>
            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row lg:justify-start sm:justify-center">
              <Button asChild size="lg">
                <Link to="/sign-up">
                  {trialLabel} <ArrowRight />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="#how">See how it works</a>
              </Button>
            </div>
            <ul className="mt-6 flex flex-wrap justify-center gap-x-5 gap-y-2 text-[13.5px] text-muted-foreground lg:justify-start">
              {['Nothing posts without your approval', 'Unlimited users', 'Cancel any time'].map((t) => (
                <li key={t} className="flex items-center gap-1.5">
                  <Check className="size-4 text-success" /> {t}
                </li>
              ))}
            </ul>
          </div>
          <ReviewPreview />
        </section>

        {/* Channels strip */}
        <section aria-label="Channels" className="border-y border-border bg-muted/40">
          <div className="mx-auto flex max-w-[1100px] flex-wrap items-center justify-center gap-x-8 gap-y-3 px-4 py-5 text-[14px] text-muted-foreground sm:px-8">
            <span className="font-semibold text-foreground">One plan, everywhere you show up:</span>
            {['Instagram', 'Facebook', 'LinkedIn', 'Bluesky', 'Email', 'Blog'].map((c) => (
              <span key={c}>{c}</span>
            ))}
          </div>
        </section>

        {/* Features */}
        <section aria-labelledby="features" className="mx-auto max-w-[1100px] px-4 py-20 sm:px-8">
          <SectionTitle id="features" eyebrow="What you get" title="A marketing team that knows your brand" sub="Built for small businesses that want to show up every week without hiring an agency." />
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {FEATURES.map((f) => (
              <Card key={f.title}>
                <CardContent className="space-y-3 py-6">
                  <div className="flex items-center gap-3">
                    <span className="flex size-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                      <f.icon className="size-5" />
                    </span>
                    <h3 className="text-[19px] font-semibold">{f.title}</h3>
                  </div>
                  <p className="text-[15px] text-muted-foreground">{f.body}</p>
                  <ul className="space-y-1.5 text-[15px]">
                    {f.points.map((p) => (
                      <li key={p} className="flex gap-2">
                        <Check className="mt-0.5 size-4 shrink-0 text-success" /> {p}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section aria-labelledby="how" id="how" className="scroll-mt-20 border-y border-border bg-muted/40">
          <div className="mx-auto max-w-[1100px] px-4 py-20 sm:px-8">
            <SectionTitle id="how-title" eyebrow="How it works" title="Four steps, every week" sub="MarketingBee does the planning and drafting. You stay in charge of what goes out." />
            <ol className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {STEPS.map((s, i) => (
                <li key={s.title} className="rounded-xl border border-border bg-card p-5">
                  <span className="flex size-8 items-center justify-center rounded-full bg-primary font-mono text-[14px] font-semibold text-primary-foreground">{i + 1}</span>
                  <h3 className="mt-4 text-[17px] font-semibold">{s.title}</h3>
                  <p className="mt-1.5 text-[14.5px] text-muted-foreground">{s.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Guardrails */}
        <section aria-labelledby="guardrails-title" id="guardrails" className="mx-auto max-w-[1100px] scroll-mt-20 px-4 py-20 sm:px-8">
          <SectionTitle id="guardrails-title" eyebrow="Guardrails" title="Honest marketing, built in" sub="Some things MarketingBee will not do, whatever the prompt." />
          <div className="mt-12 grid gap-x-8 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
            {GUARDRAILS.map((g) => (
              <div key={g.title} className="flex gap-4">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <g.icon className="size-5" />
                </span>
                <div>
                  <h3 className="text-[16px] font-semibold">{g.title}</h3>
                  <p className="mt-1 text-[14.5px] text-muted-foreground">{g.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Channels detail */}
        <section aria-labelledby="channels-title" className="border-y border-border bg-muted/40">
          <div className="mx-auto max-w-[1100px] px-4 py-20 sm:px-8">
            <SectionTitle id="channels-title" eyebrow="Channels" title="One plan, everywhere you show up" sub="Each week’s plan covers your social accounts, your list and your blog, so every piece points the same way." />
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {CHANNELS.map((c, i) => {
                const Icon = [Sparkles, Sparkles, Mail, FileText][i]!;
                return (
                  <div key={c.title} className="rounded-xl border border-border bg-card p-5">
                    <Icon className="size-5 text-primary" />
                    <h3 className="mt-3 text-[16px] font-semibold">{c.title}</h3>
                    <p className="mt-1.5 text-[14px] text-muted-foreground">{c.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Konekto */}
        <section aria-labelledby="konekto-title" className="mx-auto max-w-[1100px] px-4 py-20 sm:px-8">
          <div className="grid items-center gap-8 rounded-2xl border border-border bg-card p-8 sm:p-10 md:grid-cols-[1.4fr_1fr]">
            <div>
              <p className="text-[13px] font-semibold uppercase tracking-[0.12em] text-primary">See it with Konekto</p>
              <h2 id="konekto-title" className="mt-2 text-[28px] font-semibold tracking-[-0.02em]">
                Built and tested on our own launch
              </h2>
              <p className="mt-3 text-[16px] text-muted-foreground">
                Konekto, the influencer-marketing workspace, runs its marketing on MarketingBee: the weekly plans, the approvals, the posts and the sign-up tracking you get on day one.
              </p>
            </div>
            <div className="flex flex-col gap-3 md:items-end">
              <Button asChild variant="outline" size="lg">
                <a href="https://konekto.io" target="_blank" rel="noopener noreferrer">
                  Visit konekto.io <ArrowRight />
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section aria-labelledby="plans" className="mx-auto max-w-[1100px] px-4 pb-20 sm:px-8">
          <SectionTitle
            id="plans"
            eyebrow="Pricing"
            title={`Two plans. ${trialDays ? `${trialDays} days` : 'Try it'} free.`}
            sub="Pay monthly, change plans or cancel any time. MarketingBee counts toward the WRKRBZ bundle discount when you run two or more Bees."
          />
          <div className="mx-auto mt-12 grid max-w-3xl gap-6 md:grid-cols-2">
            {plans.data?.plans.map((p) => (
              <Card key={p.id} className={p.badge ? 'ring-2 ring-primary' : ''}>
                <CardContent className="space-y-4 py-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-[18px] font-semibold">{p.name}</h3>
                    {p.badge ? <Badge variant="primary">{p.badge}</Badge> : null}
                  </div>
                  <p className="font-mono text-[30px] font-semibold">
                    {formatMoney(p.priceCents)} <span className="text-[14px] font-normal text-muted-foreground">CAD / month</span>
                  </p>
                  <ul className="space-y-1.5 text-[14px]">
                    {p.highlights.map((h) => (
                      <li key={h} className="flex gap-2">
                        <Check className="mt-0.5 size-4 shrink-0 text-success" /> {h}
                      </li>
                    ))}
                  </ul>
                  <Button asChild className="w-full">
                    <Link to="/sign-up" search={{ plan: p.id }}>
                      Start free trial
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="mt-6 text-center text-[13px] text-muted-foreground">
            Prices in Canadian dollars and exclude applicable taxes.{' '}
            <Link to="/pricing" className="text-primary hover:underline">
              Compare plans in detail
            </Link>
          </p>
        </section>

        {/* FAQ */}
        <section aria-labelledby="faq-title" className="border-t border-border bg-muted/40">
          <div className="mx-auto max-w-[760px] px-4 py-20 sm:px-8">
            <SectionTitle id="faq-title" eyebrow="Questions" title="Good to know" />
            <div className="mt-10 divide-y divide-border rounded-xl border border-border bg-card">
              {FAQ.map((f) => (
                <details key={f.q} className="group px-5 py-4">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-[16px] font-semibold">
                    {f.q}
                    <span className="text-muted-foreground transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-2 text-[15px] text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="mx-auto max-w-[1100px] px-4 py-20 text-center sm:px-8">
          <h2 className="text-[32px] font-semibold tracking-[-0.02em] sm:text-[38px]">Ready to put a bee on your marketing?</h2>
          <p className="mx-auto mt-3 max-w-xl text-[16px] text-muted-foreground">
            Try MarketingBee free{trialDays ? ` for ${trialDays} days` : ''}. Set up your Brand Brain and approve your first weekly plan.
          </p>
          <Button asChild size="lg" className="mt-8">
            <Link to="/sign-up">
              {trialLabel} <ArrowRight />
            </Link>
          </Button>
        </section>
      </main>

      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-[1100px] flex-col items-center justify-between gap-3 px-4 py-8 text-[13px] text-muted-foreground sm:flex-row sm:px-8">
          <span>
            MarketingBee by{' '}
            <a href="https://wrkrbz.com" className="hover:text-foreground">
              WRKRBZ
            </a>
          </span>
          <nav className="flex gap-4">
            <Link to="/pricing" className="hover:text-foreground">
              Pricing
            </Link>
            <Link to="/sign-in" className="hover:text-foreground">
              Sign in
            </Link>
            <Link to="/sign-up" className="hover:text-foreground">
              Start free trial
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
